import { Map } from 'immutable';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Store } from 'redux';
import { Selector } from 'reselect';

import { ComposeAppContext } from './composeAppContext';
import { IModule, IReactComponent } from './types';

import { createStoreWithModules } from './createStore';
import { dispatch, setDispatch } from './dispatch';

export const appElement = document.getElementById('app');

export interface IComposeAppRendererParams<PROPS extends {}> {
  store: Store<Map<string, any>>;
  rootView: IReactComponent<PROPS>;
  selector: Selector<Map<string, any>, PROPS>;
}

export function ComposeAppRenderer<S>({ store, selector, rootView }: IComposeAppRendererParams<S>) {
  const state = useStoreState(store);
  const rootViewProps = useMemo(() => selector(state), [state, selector]);
  const App = rootView;
  return (
    <ComposeAppContext.Provider value={{ store, state }}>
      <App {...rootViewProps} />
    </ComposeAppContext.Provider>
  );
}

const useStoreState = (store: Store) => {
  const [state, setState] = useState<Map<string, any>>(store.getState());
  useEffect(
    () =>
      store.subscribe(() => {
        setState(store.getState());
      }),
    [store]
  );
  return state;
};

/**
 * A React Component to manage your ComposeApp state
 *
 * @param props - The modules and child elements for your application
 */
export const ComposeApp: React.FunctionComponent<{ modules: IModule<any, any, any>[] }> = ({ modules, children }) => {
  const store = useMemo(() => {
    const storeWithModules = createStoreWithModules(modules);
    setDispatch(storeWithModules.dispatch);
    dispatch({ type: 'APP_INITIALIZED' });
    return storeWithModules;
  }, [modules]);
  const state = useStoreState(store);
  return <ComposeAppContext.Provider value={{ store, state }}>{children}</ComposeAppContext.Provider>;
};

export function renderInContainer<S>(params: IComposeAppRendererParams<S>): void {
  const { rootView, store } = params;

  ReactDOM.render(<ComposeAppRenderer store={store} rootView={rootView} selector={params.selector} />, appElement);
}

export function renderPureReactDOM<S extends Map<string, any>>(
  rootView: IReactComponent<{ state: S }>
): (store: Store<S>) => void {
  return (store) => {
    return ReactDOM.render(
      <ComposeAppRenderer store={store} rootView={rootView} selector={(state) => ({ state: state as any })} />,
      appElement
    );
  };
}
